/**
 * InboxDropdown - Portaled dropdown for inbox threads preview
 *
 * Phase 4 v2 - Separate from NotificationDropdown
 *
 * Features:
 * - Portaled to body with z-40 (dropdown layer)
 * - Collision handling (clamp to viewport)
 * - Position listeners (scroll, resize, sidebar-collapse-change)
 * - Shows last 5 inbox threads with "View all" link
 * - Orange-themed (distinct from teal notifications)
 * - React Query caches inbox threads (no local useState)
 */

import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import type { RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Inbox, User } from 'lucide-react'; // Inbox used in empty state
import { useAuth } from '../contexts/AuthContext';
import { useInboxThreadsPreview, type InboxThread } from '../hooks/useInboxQuery';
import { queryKeys } from '../lib/queryKeys';
import { Z_INDEX } from '../utils/z-index';
import { formatDistanceToNow } from '../utils/dateUtils';
import { InboxDropdownSkeleton } from './Skeleton';

interface InboxDropdownProps {
  anchorRef: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
}

export function InboxDropdown({ anchorRef, onClose }: InboxDropdownProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, right: 0 });
  // Gate portal render until position is computed to prevent flash at (0,0)
  const [positionReady, setPositionReady] = useState(false);

  // React Query hook - inbox threads preview
  const { threads, totalUnread, isLoading } = useInboxThreadsPreview({ limit: 5 });

  const showLoadingSkeleton = isLoading && threads.length === 0;

  // Calculate position relative to anchor
  const updatePosition = useCallback(() => {
    if (!anchorRef.current) return;

    const rect = anchorRef.current.getBoundingClientRect();
    const dropdownWidth = 360;
    const dropdownMaxHeight = 400;

    // Position below the anchor, aligned to the right
    let top = rect.bottom + 8;
    let right = window.innerWidth - rect.right;

    // Collision detection - clamp to viewport
    // Vertical: if dropdown would go below viewport, position above
    if (top + dropdownMaxHeight > window.innerHeight - 16) {
      top = Math.max(16, rect.top - dropdownMaxHeight - 8);
    }

    // Horizontal: ensure dropdown stays within viewport
    if (right + dropdownWidth > window.innerWidth - 16) {
      right = window.innerWidth - dropdownWidth - 16;
    }
    if (right < 16) {
      right = 16;
    }

    setPosition({ top, right });
    setPositionReady(true);
  }, [anchorRef]);

  // Use useLayoutEffect for initial position to prevent flash at (0,0)
  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition]);

  // Invalidate inbox queries on mount (fresh data on open)
  useEffect(() => {
    if (user?.id) {
      queryClient.invalidateQueries({ queryKey: queryKeys.inbox.threadsPreview(user.id, { limit: 5 }) });
      queryClient.invalidateQueries({ queryKey: queryKeys.inbox.unreadCount(user.id) });
    }
  }, [user?.id, queryClient]);

  // Position listeners
  useEffect(() => {
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('sidebar-collapse-change', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('sidebar-collapse-change', updatePosition);
    };
  }, [updatePosition]);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        anchorRef.current &&
        !anchorRef.current.contains(target)
      ) {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [anchorRef, onClose]);

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Handle thread click - navigate and close dropdown
  const handleThreadClick = (thread: InboxThread) => {
    if (thread.from_user.username) {
      navigate(`/inbox/@${thread.from_user.username}`);
      onClose();
    }
  };

  // Don't render portal until position is computed to prevent flash at (0,0)
  if (!positionReady) {
    return null;
  }

  return createPortal(
    <div
      ref={dropdownRef}
      className="fixed w-[360px] max-h-[400px] bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg shadow-xl shadow-black/40 overflow-hidden flex flex-col"
      style={{
        top: position.top,
        right: position.right,
        zIndex: Z_INDEX.dropdown,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.08]">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-white">Inbox</h3>
          {totalUnread > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-orange-500 text-white rounded-full">
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </div>
      </div>

      {/* Threads list */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        {showLoadingSkeleton ? (
          <div>
            {[...Array(3)].map((_, i) => (
              <InboxDropdownSkeleton key={i} />
            ))}
          </div>
        ) : threads.length === 0 ? (
          <div className="p-8 text-center">
            <Inbox size={32} className="mx-auto mb-2 text-slate-600" />
            <p className="text-sm text-slate-400">No messages yet</p>
          </div>
        ) : (
          <ul>
            {threads.map((thread) => (
              <li key={thread.from_user_id}>
                <button
                  onClick={() => handleThreadClick(thread)}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-white/[0.04] transition-colors ${
                    thread.unread_count > 0 ? 'bg-orange-500/5' : ''
                  }`}
                >
                  {/* Avatar */}
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden">
                    {thread.from_user.avatar_url ? (
                      <img
                        src={thread.from_user.avatar_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User size={16} className="text-slate-400" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-200">
                      <span className="font-medium">
                        {thread.from_user.display_name || thread.from_user.username || 'Unknown'}
                      </span>{' '}
                      <span className="text-slate-400">{thread.preview_text}</span>
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {formatDistanceToNow(thread.last_created_at)}
                    </p>
                  </div>

                  {/* Unread indicator */}
                  {thread.unread_count > 0 && (
                    <div className="flex-shrink-0 w-2 h-2 mt-2 bg-orange-400 rounded-full" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Footer - View all link */}
      <Link
        to="/inbox"
        onClick={onClose}
        className="block px-4 py-3 text-center text-sm text-teal-400 hover:text-teal-300 hover:bg-white/[0.04] border-t border-white/[0.08] transition-colors"
      >
        View all messages
      </Link>
    </div>,
    document.body
  );
}
